/*
 *  orders/types/types.ts
 *  Interface représentant une commande telle qu'utilisée dans l'application React et l'application API
 *  Contient également l'interface pour les indices de commandes (versions)
 *  Ainsi que l'interface pour les lignes de commandes.
 */

import { ServiceData, TaxRateData } from '../../services';
import { ProductData } from '../../products';
import { ProjectData } from 'features/projects';
import { CostCenterData } from 'features/costCenter';
import type { StatusConfig } from '../../../common/components/StatusCard/StatusCard';
import {
	faCircleCheck,
	faEnvelopeCircleCheck,
	faFileAlt,
	faPaperPlane,
	faSpinner,
	faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { DocumentData } from 'features/documents/types/types';
import { UserData } from '@users/index';

export const typeOptions = [
	{
		key: 'UNIQUE',
		label: 'Unique',
	},
	{
		key: 'ANNUAL',
		label: 'Annuelle',
	},
	{
		key: 'REGULATION',
		label: 'Régulation',
	},
];

export const signatoryOptions = [
	{
		key: 'F.ROUCAYROL ADMINISTRATEUR EDF',
		label: 'F.ROUCAYROL ADMINISTRATEUR EDF',
	},
	{
		key: 'P.RISTORCELLI ADMINISTRATEUR FRAMATOME',
		label: 'P.RISTORCELLI ADMINISTRATEUR FRAMATOME',
	},
	{
		key: 'F. ROUCAYROL et P. RISTORCELLI les Administrateurs du GIE CETIC',
		label: 'F. ROUCAYROL et P. RISTORCELLI les Administrateurs du GIE CETIC',
	},
];

export interface OrderLineData {
	workflowLock?: { locked: boolean; message: string; remedy: string } | null;
	// Obligatoires
	id: number;
	label: string;
	// Champs simples
	description?: string;
	deliveryDate?: string;
	expenseType?: string;
	accountingCode?: string;
	unit?: string;
	quantity?: number;
	unitPrice?: number;
	totalExclTax?: number;
	deliveredAt?: Date;
	totalSpent?: number;
	// Relations
	service?: ServiceData | null;
	product?: ProductData | null;
	taxRate?: TaxRateData | null;
	orderVersion?: OrderVersionData;
	deliveredPercent?: number;
	project?: ProjectData | null; // Lié si expenseType = PROJECT
	costCenter?: CostCenterData | null; // Lié si expenseType = OPERATIONAL
	// Metadata
}

export interface OrderVersionData {
	workflowLock?: { locked: boolean; message: string; remedy: string } | null;
	// Obligatoires
	id: number;
	// Champs simples
	label: string;
	status?: string;
	versionIndex: number;
	commentIndex?: string;
	deliveryDate?: string;
	fiscalYear?: number;
	generatedText?: string;
	tiersReference?: string;
	signatory?: string;
	publicNotes?: string;
	acknowledgedDate?: Date;
	deliveredAt?: Date;
	editedAt?: string;
	/** Total HT calculé (somme des lignes de commande), retourné par le backend */
	totalExclTax?: number;
	// Relations
	businessOrder: OrderData;
	orderLines: OrderLineData[];
	document?: DocumentData | null;
	assignedUser?: UserData | null;
	createdBy?: {
		firstName?: string;
		name: string;
	} | null;
	// Affaires liées à cette version (calculé depuis les projets des lignes de commande)
	linkedProjects?: { id: number; referenceNumber: string }[];
	linkedSupplierInvoices?: { id: number; referenceNumber: string }[];
	// Metadata
	createdAt?: string;
}

export interface OrderData {
	// Obligatoires
	id: number;
	referenceNumber: string;
	// Champs simples
	type?: string;
	// TODO : Champs complexes à passer en relations
	tiers?: {
		id: number;
		label: string;
		email?: string;
		contacts?: {
			id: number;
			firstName: string;
			lastName: string;
			workPhone?: string;
			mobilePhone?: string;
			email?: string;
			position?: string;
			businessManager?: boolean;
			status: boolean;
		}[];
		addresses?: {
			id: number;
			types: {
				code: string;
			}[];
			label: string;
			line1: string;
			line2?: string;
			zipCode: string;
			city: string;
			country: string;
			status: boolean;
		}[];
	} | null;
	tiersType?: {
		code: string;
		label: string;
	};
	// Relations
	orderVersions?: OrderVersionData[];
	// Metadata
	createdAt?: string;
	createdBy?: {
		firstName: string;
		name: string;
	};
}

export const ORDER_STATUS_CONFIG: StatusConfig = {
	steps: [
		{
			key: 'CANCELED',
			icon: faTimesCircle,
			color: 'bg-red-600 text-white',
			textColor: 'text-red-600',
			label: 'Annulée',
			side: true,
		},
		{
			key: 'DRAFT',
			icon: faFileAlt,
			color: 'bg-blue-600 text-white',
			textColor: 'text-blue-600',
			label: 'Brouillon',
		},
		{
			key: 'SENT',
			icon: faPaperPlane,
			color: 'bg-purple-500 text-white',
			textColor: 'text-purple-600',
			label: 'Envoyée',
		},
		{
			key: 'ACKNOWLEDGED',
			icon: faEnvelopeCircleCheck,
			color: 'bg-purple-600 text-white',
			textColor: 'text-purple-600',
			label: 'AR reçu',
		},
		{
			key: 'PARTIALLY_DELIVERED',
			icon: faSpinner,
			color: 'bg-blue-600 text-white',
			textColor: 'text-blue-600',
			label: 'Partiellement livrée',
		},
		{
			key: 'DELIVERED',
			icon: faCircleCheck,
			color: 'bg-green-600 text-white',
			textColor: 'text-green-600',
			label: 'Livrée',
		},
	],

	transitions: {
		CANCELED: [{ to: 'DRAFT', label: 'Brouillon', type: 'main' }],
		DRAFT: [
			{ to: 'CANCELED', label: 'Annuler', type: 'secondary' },
			{ to: 'SENT', label: 'Envoyer', type: 'main' },
		],
		SENT: [
			{ to: 'DRAFT', label: 'Brouillon', type: 'secondary' },
			{ to: 'ACKNOWLEDGED', label: 'Valider', type: 'main' },
		],
		ACKNOWLEDGED: [
			{ to: 'SENT', label: 'Envoyé', type: 'secondary' },
			{ to: 'PARTIALLY_DELIVERED', label: 'Recept. partielle', type: 'main' },
		],
		PARTIALLY_DELIVERED: [
			{ to: 'ACKNOWLEDGED', label: 'AR reçu', type: 'secondary' },
			{ to: 'DELIVERED', label: 'Réception', type: 'main' },
		],
		DELIVERED: [
			{
				to: 'CANCELED',
				label: 'Annuler',
				type: 'secondary',
				alert: 'Êtes-vous sûr de vouloir annuler cette version de commande ?',
			},
		],
	},
};
